import React, { useRef, useCallback, useEffect, useState } from 'react';
import type { CanvasElement, CanvasElementType } from '../../../services/feedWatch';
import {
  MIN_ELEMENT_SIZE,
  HANDLE_SIZE,
  CANVAS_PADDING,
  SNAP_THRESHOLD,
  HANDLES,
} from '../../canvas-shared/canvasConstants';
import type { HandleDirection, Interaction, SnapGuide } from '../../canvas-shared/canvasConstants';
import { truncateText, resolveTokens as resolveTokensUtil } from '../../canvas-shared/canvasUtils';
import { EmptyCanvasPlaceholder } from '../../canvas-shared/EmptyCanvasPlaceholder';

// ── Helpers ──────────────────────────────────────────────────

function buildShadowString(shadow?: { x: number; y: number; blur: number; color: string }): string {
  if (!shadow) return 'none';
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
}

// ── Element Renderer ─────────────────────────────────────────

function renderElementContent(
  el: CanvasElement,
  resolveValues: boolean,
  sampleData?: Record<string, string>,
) {
  const resolve = (str: string) =>
    resolveValues ? resolveTokensUtil(str, sampleData) : str;

  switch (el.type) {
    case 'text': {
      let resolved = resolve(el.content);
      const useHtml = el.renderHtml ?? false;
      const maxChars = el.maxChars ?? 0;

      if (maxChars > 0) {
        resolved = truncateText(resolved, maxChars, useHtml);
      }

      return (
        <div
          style={{
            fontSize: el.fontSize,
            fontWeight: el.fontWeight,
            fontFamily: el.fontFamily,
            color: el.color,
            textAlign: el.textAlign,
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            wordBreak: 'break-word',
            lineHeight: 1.3,
          }}
        >
          {useHtml ? (
            <div dangerouslySetInnerHTML={{ __html: resolved }} />
          ) : (
            <span style={{ whiteSpace: 'pre-wrap' }}>{resolved}</span>
          )}
        </div>
      );
    }
    case 'image': {
      const rawSrc = resolve(el.src);
      const isToken = !rawSrc || rawSrc.startsWith('{{');
      const src = (!isToken && el.removeBg && el.bgRemovedSrc) || rawSrc;
      return isToken ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.04)',
            border: '1px dashed rgba(0,0,0,0.2)',
            borderRadius: 4,
            fontSize: 12,
            color: 'rgba(0,0,0,0.4)',
          }}
        >
          {src || 'No image'}
        </div>
      ) : (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            if (target.nextElementSibling) {
              (target.nextElementSibling as HTMLElement).style.display = 'flex';
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: el.objectFit,
            borderRadius: el.borderRadius,
            display: 'block',
          }}
        />
      );
    }
    case 'rect':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: el.fill,
            borderRadius: el.borderRadius,
            border: el.border || 'none',
          }}
        />
      );
    case 'circle':
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: el.fill,
            borderRadius: '50%',
          }}
        />
      );
    default:
      return null;
  }
}

// ── Component ────────────────────────────────────────────────

interface CanvasWorkspaceProps {
  elements: CanvasElement[];
  selectedIndex: number | null;
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  isPreview: boolean;
  sampleData?: Record<string, string>;
  showGrid?: boolean;
  onSelectElement: (index: number | null) => void;
  onUpdateElement: (index: number, updates: Partial<CanvasElement>) => void;
  onAddElement?: (type: CanvasElementType) => void;
}

export function CanvasWorkspace({
  elements,
  selectedIndex,
  canvasWidth,
  canvasHeight,
  zoom,
  isPreview,
  sampleData,
  showGrid,
  onSelectElement,
  onUpdateElement,
  onAddElement,
}: CanvasWorkspaceProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [interaction, setInteraction] = useState<Interaction | null>(null);
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);

  const scaledW = canvasWidth * zoom;
  const scaledH = canvasHeight * zoom;

  // ── Coordinate conversion ──
  const toCanvasCoords = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: (clientX - rect.left) / zoom,
        y: (clientY - rect.top) / zoom,
      };
    },
    [zoom],
  );

  // ── Snap guides ──
  const computeSnap = useCallback(
    (_elIndex: number, newX: number, newY: number, w: number, h: number) => {
      const guides: SnapGuide[] = [];
      let snappedX = newX;
      let snappedY = newY;

      const cx = canvasWidth / 2;
      const cy = canvasHeight / 2;
      const elCx = newX + w / 2;
      const elCy = newY + h / 2;

      if (Math.abs(elCx - cx) < SNAP_THRESHOLD) {
        snappedX = cx - w / 2;
        guides.push({ orientation: 'vertical', position: cx });
      }
      if (Math.abs(elCy - cy) < SNAP_THRESHOLD) {
        snappedY = cy - h / 2;
        guides.push({ orientation: 'horizontal', position: cy });
      }

      if (Math.abs(newX) < SNAP_THRESHOLD) { snappedX = 0; guides.push({ orientation: 'vertical', position: 0 }); }
      if (Math.abs(newY) < SNAP_THRESHOLD) { snappedY = 0; guides.push({ orientation: 'horizontal', position: 0 }); }
      if (Math.abs(newX + w - canvasWidth) < SNAP_THRESHOLD) { snappedX = canvasWidth - w; guides.push({ orientation: 'vertical', position: canvasWidth }); }
      if (Math.abs(newY + h - canvasHeight) < SNAP_THRESHOLD) { snappedY = canvasHeight - h; guides.push({ orientation: 'horizontal', position: canvasHeight }); }

      return { snappedX, snappedY, guides };
    },
    [canvasWidth, canvasHeight],
  );

  // ── Drag / resize interaction ──
  useEffect(() => {
    if (!interaction) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { x: mouseX, y: mouseY } = toCanvasCoords(e.clientX, e.clientY);
      const dx = mouseX - interaction.startMouseX;
      const dy = mouseY - interaction.startMouseY;

      if (interaction.mode === 'rotate') {
        const el = elements[interaction.elementIndex];
        const cx = el.x + el.width / 2;
        const cy = el.y + el.height / 2;
        const currentAngle = Math.atan2(mouseY - cy, mouseX - cx);
        const deltaAngle = currentAngle - interaction.startAngle!;
        const degrees = (interaction.startRotation! + deltaAngle * (180 / Math.PI)) % 360;
        onUpdateElement(interaction.elementIndex, {
          rotation: Math.round(degrees < 0 ? degrees + 360 : degrees),
        });
        return;
      }

      if (interaction.mode === 'drag') {
        const rawX = Math.round(interaction.startElX + dx);
        const rawY = Math.round(interaction.startElY + dy);
        const { snappedX, snappedY, guides } = computeSnap(
          interaction.elementIndex, rawX, rawY,
          interaction.startElW, interaction.startElH,
        );
        setSnapGuides(guides);
        onUpdateElement(interaction.elementIndex, { x: snappedX, y: snappedY });
        return;
      }

      // Resize
      const dir = interaction.handleDir!;
      let newX = interaction.startElX;
      let newY = interaction.startElY;
      let newW = interaction.startElW;
      let newH = interaction.startElH;

      if (dir.includes('e')) newW = Math.max(MIN_ELEMENT_SIZE, interaction.startElW + dx);
      if (dir.includes('w')) {
        newW = Math.max(MIN_ELEMENT_SIZE, interaction.startElW - dx);
        newX = interaction.startElX + (interaction.startElW - newW);
      }
      if (dir.includes('s')) newH = Math.max(MIN_ELEMENT_SIZE, interaction.startElH + dy);
      if (dir.includes('n')) {
        newH = Math.max(MIN_ELEMENT_SIZE, interaction.startElH - dy);
        newY = interaction.startElY + (interaction.startElH - newH);
      }

      const el = elements[interaction.elementIndex];
      if (el.type === 'circle') {
        const size = Math.max(newW, newH);
        newW = size;
        newH = size;
      }

      onUpdateElement(interaction.elementIndex, {
        x: Math.round(newX),
        y: Math.round(newY),
        width: Math.round(newW),
        height: Math.round(newH),
      });
    };

    const handleMouseUp = () => {
      setInteraction(null);
      setSnapGuides([]);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [interaction, toCanvasCoords, onUpdateElement, elements, computeSnap]);

  // ── Event handlers ──

  const handleBackgroundMouseDown = useCallback(() => {
    onSelectElement(null);
  }, [onSelectElement]);

  const handleElementMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      onSelectElement(index);

      const el = elements[index];
      if (el.locked) return;

      const { x, y } = toCanvasCoords(e.clientX, e.clientY);

      setInteraction({
        mode: 'drag',
        elementIndex: index,
        startMouseX: x,
        startMouseY: y,
        startElX: el.x,
        startElY: el.y,
        startElW: el.width,
        startElH: el.height,
      });
    },
    [elements, onSelectElement, toCanvasCoords],
  );

  const handleHandleMouseDown = useCallback(
    (e: React.MouseEvent, index: number, dir: HandleDirection) => {
      e.stopPropagation();
      const el = elements[index];
      if (el.locked) return;

      const { x, y } = toCanvasCoords(e.clientX, e.clientY);

      setInteraction({
        mode: 'resize',
        elementIndex: index,
        startMouseX: x,
        startMouseY: y,
        startElX: el.x,
        startElY: el.y,
        startElW: el.width,
        startElH: el.height,
        handleDir: dir,
      });
    },
    [elements, toCanvasCoords],
  );

  const handleRotateMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      const el = elements[index];
      if (el.locked) return;

      const { x: mouseX, y: mouseY } = toCanvasCoords(e.clientX, e.clientY);
      const cx = el.x + el.width / 2;
      const cy = el.y + el.height / 2;
      const startAngle = Math.atan2(mouseY - cy, mouseX - cx);

      setInteraction({
        mode: 'rotate',
        elementIndex: index,
        startMouseX: mouseX,
        startMouseY: mouseY,
        startElX: el.x,
        startElY: el.y,
        startElW: el.width,
        startElH: el.height,
        startAngle,
        startRotation: el.rotation ?? 0,
      });
    },
    [elements, toCanvasCoords],
  );

  const handleVisualSize = HANDLE_SIZE / zoom;
  const handleHalf = handleVisualSize / 2;

  // Grid background pattern
  const gridBg = showGrid
    ? {
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
      }
    : {};

  return (
    <div
      className="flex-1 min-w-0 overflow-auto"
      style={{ backgroundColor: '#e8e8e8' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '100%',
          minHeight: '100%',
          width: scaledW + CANVAS_PADDING * 2,
          height: scaledH + CANVAS_PADDING * 2,
        }}
      >
        <div
          style={{
            width: scaledW,
            height: scaledH,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)',
            flexShrink: 0,
          }}
        >
          <div
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: canvasWidth,
              height: canvasHeight,
              backgroundColor: '#ffffff',
              transform: `scale(${zoom})`,
              transformOrigin: '0 0',
              ...gridBg,
            }}
          >
            {/* Empty canvas placeholder */}
            {elements.length === 0 && onAddElement && (
              <EmptyCanvasPlaceholder onAddElement={onAddElement} />
            )}

            {/* Snap guides */}
            {snapGuides.map((guide, i) => (
              <div
                key={i}
                data-export-ignore="true"
                style={{
                  position: 'absolute',
                  ...(guide.orientation === 'vertical'
                    ? { left: guide.position, top: 0, width: 1 / zoom, height: canvasHeight, backgroundColor: 'hsl(var(--primary))' }
                    : { left: 0, top: guide.position, width: canvasWidth, height: 1 / zoom, backgroundColor: 'hsl(var(--primary))' }
                  ),
                  opacity: 0.5,
                  pointerEvents: 'none',
                  zIndex: 100,
                }}
              />
            ))}

            {elements.map((el, idx) => {
              const isSelected = selectedIndex === idx;
              const isVisible = el.visible !== false;
              if (!isVisible) return null;

              return (
                <div
                  key={el.id || idx}
                  onMouseDown={(e) => handleElementMouseDown(e, idx)}
                  style={{
                    position: 'absolute',
                    left: el.x,
                    top: el.y,
                    width: el.width,
                    height: el.height,
                    transform: `rotate(${el.rotation ?? 0}deg)`,
                    transformOrigin: 'center center',
                    opacity: el.opacity ?? 1,
                    boxShadow: buildShadowString(el.shadow),
                    outline: isSelected
                      ? `${2 / zoom}px solid hsl(var(--primary))`
                      : 'none',
                    outlineOffset: `${-1 / zoom}px`,
                    cursor: el.locked
                      ? 'default'
                      : interaction?.mode === 'drag' && interaction.elementIndex === idx
                        ? 'grabbing'
                        : 'grab',
                    userSelect: 'none',
                    overflow: 'hidden',
                  }}
                >
                  {renderElementContent(el, isPreview, sampleData)}

                  {/* Resize handles */}
                  {isSelected && !el.locked &&
                    HANDLES.map((handle) => {
                      const pos = handle.getPos(el.width, el.height, handleHalf);
                      return (
                        <div
                          key={handle.dir}
                          data-export-ignore="true"
                          onMouseDown={(e) => handleHandleMouseDown(e, idx, handle.dir)}
                          style={{
                            position: 'absolute',
                            left: pos.x,
                            top: pos.y,
                            width: handleVisualSize,
                            height: handleVisualSize,
                            backgroundColor: 'hsl(var(--primary))',
                            border: `${1.5 / zoom}px solid hsl(var(--background))`,
                            borderRadius: 2 / zoom,
                            cursor: handle.cursor,
                            zIndex: 10,
                          }}
                        />
                      );
                    })}

                  {/* Rotation zones */}
                  {isSelected && !el.locked &&
                    (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                      const rotZoneSize = 12 / zoom;
                      const offset = rotZoneSize + handleHalf;
                      const positions: Record<string, { left: number; top: number }> = {
                        nw: { left: -offset, top: -offset },
                        ne: { left: el.width + handleHalf, top: -offset },
                        sw: { left: -offset, top: el.height + handleHalf },
                        se: { left: el.width + handleHalf, top: el.height + handleHalf },
                      };
                      const pos = positions[corner];
                      return (
                        <div
                          key={`rot-${corner}`}
                          data-export-ignore="true"
                          onMouseDown={(e) => handleRotateMouseDown(e, idx)}
                          style={{
                            position: 'absolute',
                            left: pos.left,
                            top: pos.top,
                            width: rotZoneSize,
                            height: rotZoneSize,
                            cursor: 'grab',
                            zIndex: 9,
                          }}
                        />
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
